import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Group, GroupPayload } from "./types";
import {
  settingsQueryKey,
  useSettings,
  putGroup,
  testGroup,
  fetchSchema,
} from "./api";
import { FormPanel } from "./panels/FormPanel";
import { AboutPanel } from "./panels/AboutPanel";
import { CollectionPanel } from "./panels/CollectionPanel";
import { CustomPanelHost, type CustomRegistry } from "./panels/CustomPanelHost";
import { VersionBadge } from "./VersionBadge";

interface Props {
  customRegistry?: CustomRegistry;
}

/** Reads / writes the active tab key to the URL query string so refresh &
 *  bookmarking work. Falls back to lowest-order group on first visit. */
function useActiveTab(groups: Group[] | undefined): [string, (k: string) => void] {
  const [active, setActive] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("tab") ?? "";
  });

  useEffect(() => {
    if (!groups || groups.length === 0) return;
    const valid = groups.some((g) => g.key === active);
    if (!valid) setActive(groups[0].key);
  }, [groups, active]);

  const update = (k: string) => {
    setActive(k);
    if (typeof window !== "undefined") {
      const u = new URL(window.location.href);
      u.searchParams.set("tab", k);
      window.history.replaceState({}, "", u.toString());
    }
  };

  return [active, update];
}

export function SettingsPage({ customRegistry = {} }: Props) {
  const schema = useQuery({
    queryKey: ["settings-schema"],
    queryFn: fetchSchema,
    staleTime: 5 * 60 * 1000,
  });

  const groups = schema.data?.groups;
  const [active, setActive] = useActiveTab(groups);

  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});

  const switchActive = useCallback(
    (nextKey: string) => {
      setDirtyMap((prev) => {
        const next = { ...prev };
        delete next[active];
        return next;
      });
      setActive(nextKey);
    },
    [active, setActive],
  );

  const reportDirty = useCallback(
    (groupKey: string, dirty: boolean) => {
      setDirtyMap((prev) => {
        const current = prev[groupKey] ?? false;
        if (current === dirty) return prev;
        if (!dirty) {
          const next = { ...prev };
          delete next[groupKey];
          return next;
        }
        return { ...prev, [groupKey]: true };
      });
    },
    [],
  );

  const activeGroup = useMemo(
    () => groups?.find((g) => g.key === active),
    [groups, active],
  );

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <header className="mb-6 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Settings</h1>
        <VersionBadge />
      </header>

      {schema.isPending && (
        <div className="text-sm text-muted-foreground">Loading schema…</div>
      )}
      {schema.error && (
        <div className="rounded-md border border-destructive-soft bg-destructive-soft p-3 text-sm text-destructive-soft-foreground">
          Failed to load schema: {String(schema.error)}
        </div>
      )}

      {groups && groups.length > 0 && (
        <>
          {/* flex-wrap behaves as "one row when it fits, wrap when
              it doesn't" — single-row for small group counts on wide
              screens, multi-row only after the natural width runs out.
              Keeps tabs tight for most deployments while preventing
              the horizontal-scroll problem the overflow-x-auto version
              had past ~8 groups. */}
          <nav className="mb-6 flex flex-wrap gap-x-1 gap-y-0.5">
            {groups.map((g) => {
              const isActive = g.key === active;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => switchActive(g.key)}
                  className={`relative whitespace-nowrap border-b px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "border-foreground text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {g.title}
                  {dirtyMap[g.key] && (
                    <span className="ml-1 text-warning" aria-label="unsaved changes">
                      ●
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {activeGroup && (
            <PanelSwitch
              group={activeGroup}
              customRegistry={customRegistry}
              onDirtyChange={(d) => reportDirty(activeGroup.key, d)}
            />
          )}
        </>
      )}
    </div>
  );
}

function PanelSwitch({
  group,
  customRegistry,
  onDirtyChange,
}: {
  group: Group;
  customRegistry: CustomRegistry;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const q = useSettings(group.key);
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  if (q.isPending) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading {group.title}…
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="rounded-lg border border-destructive-soft bg-destructive-soft p-6 text-sm text-destructive-soft-foreground">
        Failed to load {group.title}: {String(q.error)}
      </div>
    );
  }
  const payload = q.data as GroupPayload;

  if (payload.kind === "form") {
    return (
      <div className="space-y-3">
        <FormPanel
          group={group}
          payload={payload}
          saving={saving}
          error={error}
          onDirtyChange={onDirtyChange}
          onSave={async (body) => {
            setSaving(true);
            setError(null);
            try {
              const fresh = await putGroup(group.key, body);
              qc.setQueryData(settingsQueryKey(group.key), fresh);
            } catch (e) {
              setError(String(e));
              throw e;
            } finally {
              setSaving(false);
            }
          }}
          onTest={
            group.actions?.includes("test")
              ? async (fields) => {
                  setTestMsg("Testing…");
                  try {
                    const res = await testGroup(group.key, fields);
                    const detail =
                      res.message ?? (res.latency_ms != null ? `${res.latency_ms}ms` : "?");
                    setTestMsg(
                      res.ok
                        ? `✓ OK (${detail})`
                        : `✗ FAIL: ${res.error ?? "unknown"}`,
                    );
                  } catch (e) {
                    setTestMsg(`✗ FAIL: ${String(e)}`);
                  }
                }
              : undefined
          }
        />
        {testMsg && (
          <div
            className={`rounded-md border px-3 py-2 text-xs ${
              testMsg.startsWith("✓")
                ? "border-success-soft bg-success-soft text-success-soft-foreground"
                : testMsg.startsWith("✗")
                ? "border-destructive-soft bg-destructive-soft text-destructive-soft-foreground"
                : "border-border bg-muted text-muted-foreground"
            }`}
          >
            {testMsg}
          </div>
        )}
      </div>
    );
  }
  if (payload.kind === "collection") {
    return <CollectionPanel group={group} payload={payload} />;
  }
  if (payload.kind === "readonly") {
    return <AboutPanel group={group} endpoint={payload.ref.endpoint} />;
  }
  return (
    <CustomPanelHost
      group={group}
      endpoint={payload.ref.endpoint}
      registry={customRegistry}
      onDirtyChange={onDirtyChange}
    />
  );
}
